/**
 * The study-hours board, beside the room.
 *
 * *** AMBIENT PRESSURE IS THE POINT ***
 * The library sells the feeling that other people are working. A board that
 * updates while you sit there is what turns recorded hours from a private
 * statistic into a reason to stay another twenty minutes.
 *
 * *** EVERY VIEWER FINDS THEMSELVES ***
 * A student ranked 47th sees ten strangers and nothing about themselves.
 * The server sends `my_rank` when they are off the end of the page, and it
 * is pinned at the bottom so the board is always a position, not a notice.
 */

import { useState } from 'react'
import { ApiError } from '@/lib/api'
import { useLeaderboard } from '@/lib/queries'
import type { LeaderboardPeriod } from '@/types/api'
import { hoursMinutes } from './format'
import styles from './RoomLeaderboard.module.css'

const PERIODS: Array<{ id: LeaderboardPeriod; label: string }> = [
  { id: 'TODAY', label: 'Today' },
  { id: 'WEEK', label: 'Week' },
  { id: 'MONTH', label: 'Month' },
  { id: 'ALL', label: 'All' },
]

/** A medal for the top three, a plain number after that. */
function rankMark(rank: number): string {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return String(rank)
}

export function RoomLeaderboard({
  viewerIsMentor = false,
}: {
  /*
    *** A MENTOR CANNOT BE ON THIS BOARD ***
    It ranks STUDY time, and a mentor's presence is supervision. Showing
    them "you have not studied in this period yet" states a rule they
    cannot act on, and pinning them at an unranked position would be worse.
    They still see the board — knowing who is working is the point of being
    in the room.
  */
  viewerIsMentor?: boolean
} = {}) {
  const [period, setPeriod] = useState<LeaderboardPeriod>('TODAY')
  const { data, isLoading, error } = useLeaderboard(period)

  const rows = data?.rows ?? []
  const meOnBoard = rows.some((r) => r.is_me)

  return (
    <section className={styles.board} aria-label="Study leaderboard">
      <h2 className={styles.title}>Leaderboard</h2>

      <div className={styles.tabs} role="tablist">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={period === p.id}
            className={`${styles.tab} ${period === p.id ? styles.tabActive : ''}`}
            onClick={() => setPeriod(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {isLoading && !data && <p className={styles.muted}>Loading…</p>}

      {error && (
        <p className={styles.muted}>
          {error instanceof ApiError
            ? error.message
            : 'Could not load the board.'}
        </p>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <p className={styles.muted}>
          Nobody has studied yet. Be the first on the board.
        </p>
      )}

      <ol className={styles.list}>
        {rows.map((row) => (
          <li
            key={row.user_id}
            className={`${styles.row} ${row.is_me ? styles.rowMe : ''}`}
          >
            <span className={styles.rank}>{rankMark(row.rank)}</span>
            <span className={styles.name}>
              {row.name}
              {row.is_me && <span className={styles.youTag}>You</span>}
            </span>
            <span className={styles.figures}>
              {/*
                *** POINTS ARE THE RANKING, HOURS ARE THE CONTEXT ***
                Sorting by one number and showing another is how a board
                stops making sense. Points lead; the time that earned them
                sits underneath.
              */}
              <span className={styles.hours}>
                {row.points.toLocaleString()} pts
              </span>
              <span className={styles.subFigure}>
                {hoursMinutes(row.active_seconds)}
              </span>
              {/* A live dot beside a number is what makes the board feel
                  like a room rather than a report. */}
              {row.is_live && <span className={styles.live}>Live</span>}
            </span>
          </li>
        ))}
      </ol>

      {/*
        Pinned only when they are NOT already on the page — showing someone
        twice reads as a bug.
      */}
      {!viewerIsMentor && !meOnBoard && data?.my_rank != null && (
        <div className={`${styles.row} ${styles.rowMe} ${styles.pinned}`}>
          <span className={styles.rank}>{data.my_rank}</span>
          <span className={styles.name}>
            You
          </span>
          <span className={styles.figures} />
        </div>
      )}

      {!viewerIsMentor &&
        !meOnBoard &&
        data &&
        data.my_rank == null &&
        rows.length > 0 && (
          <p className={styles.muted}>
            You have not studied in this period yet.
          </p>
        )}
    </section>
  )
}
